<template>
  <div class="w-100">
    <SelectAgent 
      ref="selectAgentRef" 
      :isInheritViewable="true" 
      :isRememberSelection="false" 
      :agent-ids="mappedAgentIds"
      :division-list="divisionList" 
      :columns="GOOGLE_ACCOUNT_SETTING_COLUMN"
      :get-cell-text-func="customGetCellText" 
      :selected-agent-ids="selectedAgentIds"
      @onChangeSelectAgent="handleAgentSelectionChange" 
      description="個別設定を行う端末を指定してください。" 
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, markRaw, watchEffect, nextTick } from 'vue';
import { DivisionDBData } from "@/models/DivisionData";
import { SearchColumnData } from '@/models/SearchColumnData';
import SelectAgent from '@/components/Assets/SelectAgent.vue';
import { AndroidSimpleAsset } from '@/views/Mobile/Android/GoogleAccountSetting/model/AndroidAsset';

// --- Props & Emits ---
const props = defineProps<{
  /** モーダルで保持されている、現在チェックが入っている端末IDのSet */
  selectedAgentIds: Set<number>;
  /** まだ設定されていないAndroid端末リスト */
  unconfiguredAgents: Array<AndroidSimpleAsset>;
  /** 部署リスト */
  divisionList: Array<DivisionDBData>;
}>();

const emit = defineEmits<{
  (e: 'update:selectedAgentIds', ids: Set<number>): void;
}>();

// --- Template Ref ---
const selectAgentRef = ref<any>(null);

// --- 列（カラム）定義 ---
const GOOGLE_ACCOUNT_SETTING_COLUMN: Array<SearchColumnData> = markRaw([
  { columnID: 0, dataType: 2, text: "端末機No." },
  { columnID: 1, dataType: 1, text: "端末機名" },
  { columnID: 2, dataType: 1, text: "コンピューター名" },
  { columnID: 3, dataType: 1, text: "部署名" },
]);

/**
 * ダイアログが開かれたタイミング(データが渡された時)に中のUIを強制初期化する。
 */
watch(() => props.unconfiguredAgents, async () => {
  // nextTick を使って DOM や ref のバインドが確実に完了した後に実行する
  await nextTick();
  if (selectAgentRef.value && typeof selectAgentRef.value.initializeData === 'function') {
    selectAgentRef.value.initializeData();
  }
});

/**
 * 汎用UI（SelectAgent）が部署連動を行うために最低限必要とする、
 * IDのペア配列（agentID と divisionID）に軽量マッピングします。
 */
const mappedAgentIds = computed(() => {
  return props.unconfiguredAgents.map(asset => ({
    agentID: asset.agentNo,
    divisionID: asset.divisionId
  }));
});

/**
 * props.unconfiguredAgents に変化があった時だけ1回再構築されます。
 */
let agentMapByNo = new Map<number, AndroidSimpleAsset>();

watchEffect(() => {
  const newMap = new Map<number, AndroidSimpleAsset>();
  for (let i = 0; i < props.unconfiguredAgents.length; i++) {
    const asset = props.unconfiguredAgents[i];
    newMap.set(asset.agentNo, asset);
  }
  agentMapByNo = newMap;
});

/**
 * 各セルに表示するテキストを取得する処理（Androidドメイン固有ロジック）
 */
const customGetCellText = (columnId: number, agentId: number): string => {
  const agent = agentMapByNo.get(agentId);
  if (!agent) return "";
  
  switch (columnId) {
    case 0:
      return String(agent.agentNo); // 端末機No.
    case 1:
      return agent.agentName;       // 端末機名
    case 2:
      return agent.computerName;    // コンピューター名
    case 3:
      return agent.divisionName;    // 部署名
  }
  return "";
};

/**
 * 選択状態の変更をそのまま上のモーダルへ通知
 */
const handleAgentSelectionChange = (newSelectedSet: Set<number>) => {
  emit('update:selectedAgentIds', newSelectedSet);
};

/**
 * 部署データを取得するメソッドを追加
 */
const getSelectedDivisionData = () => {
  if (selectAgentRef.value) {
    return {
      divisionId: selectAgentRef.value.selectDivision?.divisionId ?? 0,
      enableChildDivision: selectAgentRef.value.btnContainsAll ?? false
    };
  }
  return { divisionId: 0, enableChildDivision: false };
};

defineExpose({
  getSelectedDivisionData
});
</script>
